/*----------------------------------
- PUBLIC SEARCH CANONICALIZATION
----------------------------------*/

import type { PublicSearchBrowseResource } from '@uniquedomains/core/domains/public-search';

type BuildPublicSearchAliasRedirectInput = {
    requestedUrl: URL;
    resolvedUrl: string | null;
};

const normalizeDomainsResourcePath = (sourceUrl: string): string | null => {
    const origin = 'https://unique.domains';
    if (!URL.canParse(sourceUrl, origin)) return null;

    const url = new URL(sourceUrl, origin);
    const pathname = url.pathname.replace(/\/+$/, '') || '/domains';
    if (pathname !== '/domains' && !pathname.startsWith('/domains/')) return null;

    return pathname;
};

export const buildPublicSearchAliasRedirect = ({
    requestedUrl,
    resolvedUrl,
}: BuildPublicSearchAliasRedirectInput): string | null => {
    if (!resolvedUrl) return null;

    const canonicalUrl = new URL(resolvedUrl, requestedUrl.origin);
    const requestedPathname = requestedUrl.pathname.replace(/\/+$/, '') || '/';
    const canonicalPathname = canonicalUrl.pathname.replace(/\/+$/, '') || '/';
    if (requestedPathname === canonicalPathname) return null;

    if (!canonicalUrl.search && requestedUrl.search) canonicalUrl.search = requestedUrl.search;
    return `${canonicalUrl.pathname}${canonicalUrl.search}`;
};

export const selectPublishedBrowseResources = (
    browseResources: readonly PublicSearchBrowseResource[] | undefined,
    renderedResourcePaths: readonly string[],
    limit = 4,
): PublicSearchBrowseResource[] => {
    const renderedResourcePathSet = new Set(renderedResourcePaths);

    return (browseResources || [])
        .filter((resource) => {
            const resourcePath = normalizeDomainsResourcePath(resource.href);
            return Boolean(resourcePath && renderedResourcePathSet.has(resourcePath));
        })
        .slice(0, limit);
};
